import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { CURRENT_VERSION } from '../changelog';
import AccountMenu from '../components/AccountMenu';
import { useConfig } from '../context/ConfigContext';
import ErrorBoundary from '../components/ErrorBoundary';
import NavGroup, { type NavGroupEntry, type NavGroupItem } from '../components/NavGroup';
import PageLoading from '../components/PageLoading';
import {
  IconAttach,
  IconFinance,
  IconHR,
  IconInvestments,
  IconMembers,
  IconReports,
  IconSystem,
} from '../components/icons';
import { hasAnyPermission } from '../utils/roles';
import vnpfLogo from '../assets/vnpf-logo.png';
import './AppShell.css';

// Nav structure:
//  - Home (announcements/calendar) is its own top-level link. There is no
//    more standalone cross-module Dashboard — it was replaced by one
//    module-specific "Dashboard" nav item inside each of the 4 main
//    NavGroups below (Memberships, Finance & Accounting, Human Resources,
//    Investment Management), each gated by that module's own existing view
//    permission rather than the retired analytics.view/analytics_viewer.
//  - Memberships is a small NavGroup (Members + Statements) — it has
//    nothing to do with investments, it's just the Fund's member register
//    plus the Mini/Detail/Annual statement views over it.
//  - Finance & Accounting is a single collapsible NavGroup covering
//    Revenue + Procurement + Resources together, with a header divider per
//    sub-domain.
//  - Human Resources is a collapsible NavGroup covering every HR
//    sub-module (Attendance through Separation).
//  - Investment Management is a small NavGroup (Investments + Policy) — the
//    individual investment register and the asset-allocation policy/
//    breach breakdown used to be one page, split apart for the same reason
//    Memberships split off Statements.
//  - Reports is a single top-level link to the report menu; every report
//    page under it enforces its own permission via RequireAuth.
//  - Announcements and Events live inside the System group, hidden from
//    every self-service identity role (Employee, Member, Supplier,
//    Customer, Employer — see SELF_SERVICE_NAV_ROLES) even though their
//    routes stay unguarded in api.php (this exclusion is nav-only, a
//    product decision, not a permission the backend enforces). They're
//    spliced into visibleSystemItems rather than listed in SYSTEM_ITEMS
//    itself (see that array's own comment for why).
// Both items share the exact same route-level permission (investments.view
// — see App.tsx), so there's no per-item filtering to do here.
const INVESTMENT_ITEMS: NavGroupItem[] = [
  { to: '/investments/dashboard', label: 'Dashboard' },
  { to: '/investments/policy', label: 'Policy' },
  { to: '/investments', label: 'Investments', end: true },
  { to: '/investments/counterparties', label: 'Counterparties' },
];

// Members/Statements share the exact same route-level permission
// (memberships.view|member.profile.view — see App.tsx). Employers is
// staff-only (memberships.view alone) — unlike Members, it has no
// self-service scoping to fall back to (see the matching route comment in
// api.php), so member.profile.view is deliberately excluded here too.
// Contribution Schedules is additionally open to the self-service employer
// role, which has neither of those, so it carries its own OR-list and
// needs per-item filtering like the Finance & Accounting group.
// Claims/Approval Levels/Payment Batches/Cheques (formerly a standalone
// "Benefit Payments" module) are folded in here as plain items — see
// RoleCatalog::MEMBERSHIP_BENEFITS_PERMISSIONS. A member-portal user only
// ever holds member.profile.view, so they see Claims (their own,
// self-scoped server-side) but not the staff-only items.
const MEMBERSHIP_ITEMS: (NavGroupItem & { permissions?: string[] })[] = [
  // Staff-only (memberships.view) — company-wide contribution/benefit/
  // penalty KPIs aren't for a member-portal user, unlike Members below.
  { to: '/memberships/dashboard', label: 'Dashboard', permissions: ['memberships.view'] },
  { to: '/memberships', label: 'Members', end: true, permissions: ['memberships.view', 'member.profile.view'] },
  { to: '/memberships/employers', label: 'Employers', permissions: ['memberships.view'] },
  {
    to: '/memberships/contribution-schedules',
    label: 'Contribution Schedules',
    permissions: ['memberships.contributions.manage', 'employer.contributions.view'],
  },
  {
    to: '/memberships/penalties',
    label: 'Penalties & Surcharges',
    permissions: ['penalties.view', 'employer.penalties.view'],
  },
  { to: '/memberships/statements', label: 'Statements', permissions: ['memberships.view', 'member.profile.view'] },
  {
    to: '/memberships/benefits/claims',
    label: 'Claims',
    permissions: ['memberships.benefits.view', 'member.profile.view'],
  },
  {
    to: '/memberships/benefits/payment-batches',
    label: 'Payment Batches',
    permissions: ['memberships.benefits.disburse'],
  },
  {
    to: '/memberships/benefits/cheques',
    label: 'Cheques',
    permissions: ['memberships.benefits.disburse', 'memberships.benefits.cheque.sign'],
  },
];

// Attendance/Timesheet, Payslips, Overtime, Performance, Training,
// Documents and Separation (self-service view only, employee's own
// records) require employee.profile.view rather than hr.view — that's the
// permission every Employee record's linked user automatically holds via
// the "Employee" role (see PortalUserProvisioner::provisionForEmployee(),
// RoleCatalog::definitions()), so a portal-only account (member/supplier/
// customer/employer) — which is never linked to an Employee record —
// doesn't see a Human Resources menu with nothing legitimately theirs in
// it; hr.view holders additionally see and manage everyone's on the same
// page. Leave, Payroll and Recruitment stay gated: Leave routes through
// an HR approval step, Payroll is sensitive pay data, and candidates
// aren't system users — none of those are ever self-service (Payslips
// and Performance are the self-service read-only views of a finalized
// Payroll entry / performance review; Training/Documents/Separation are
// self-service view only — HR still creates/edits every record; approved
// Overtime feeds into Payroll as an addition line — see
// PayrollRunController::addOvertimeLine()).
// hr.view holders see and manage everyone's records on these self-service
// pages, so each keeps hr.view as an alternate — losing employee.profile.view
// (no linked Employee record) shouldn't also hide a page an HR role is
// otherwise meant to administer.
const HR_ITEMS: (NavGroupItem & { permissions?: string[] })[] = [
  // Staff-only (hr.view) — company-wide headcount/leave/attendance figures
  // aren't for a self-service employee.profile.view holder.
  { to: '/hr/dashboard', label: 'Dashboard', permissions: ['hr.view'] },
  {
    to: '/hr/employees',
    label: 'Employees',
    // employee.profile.view holders reach the same page — self-scoped to
    // their own record and read-only, see EmployeeForm.tsx.
    permissions: ['hr.view', 'employee.profile.view'],
    // Everything under /hr/employees is the Employees flow except the
    // per-employee Pay page, which is reached via Compensation instead.
    isActive: (path) => path.startsWith('/hr/employees') && !/^\/hr\/employees\/\d+\/pay$/.test(path),
  },
  { to: '/hr/documents', label: 'Documents', permissions: ['employee.profile.view', 'hr.view'] },
  { to: '/hr/positions', label: 'Positions', permissions: ['hr.view'] },
  { to: '/hr/training', label: 'Training', permissions: ['employee.profile.view', 'hr.view'] },
  { to: '/hr/performance', label: 'Performance', permissions: ['employee.profile.view', 'hr.view'] },
  { to: '/hr/leave', label: 'Leave', permissions: ['hr.view'] },
  { to: '/hr/attendance', label: 'Attendance', permissions: ['employee.profile.view', 'hr.view'] },
  { to: '/hr/overtime', label: 'Overtime', permissions: ['employee.profile.view', 'hr.view'] },
  {
    to: '/hr/compensation',
    label: 'Compensation',
    permissions: ['hr.view'],
    isActive: (path) => path === '/hr/compensation' || /^\/hr\/employees\/\d+\/pay$/.test(path),
  },
  { to: '/hr/payroll', label: 'Payroll', permissions: ['hr.view'] },
  { to: '/hr/payslips', label: 'Payslips', permissions: ['employee.profile.view', 'hr.view'] },
  { to: '/hr/recruitment', label: 'Recruitment', permissions: ['hr.view'] },
  { to: '/hr/separation', label: 'Separation', permissions: ['hr.view'] },
];

// Each item's `permissions` mirrors the exact `permission:a|b|c` OR-list
// that gates its route in api.php, so a nav link is never shown for
// something the backend would 403 for — and never hidden for a permission
// combo that would work. Grouped into sub-headers within the one Finance &
// Accounting NavGroup per module.
interface FinanceItem extends NavGroupItem {
  permissions: string[];
}

const REVENUE_ITEMS: FinanceItem[] = [
  { to: '/revenue/customers', label: 'Customers', permissions: ['revenue.customers.view', 'customer.profile.view'] },
  { to: '/revenue/quotations', label: 'Quotations', permissions: ['revenue.view', 'customer.profile.view'] },
  {
    to: '/revenue/invoices',
    label: 'Invoices',
    // member.profile.view deliberately excluded — see the matching route
    // comment in api.php: a member's own invoices/receipts aren't
    // surfaced in the portal, their contribution Statements cover that.
    permissions: ['revenue.view', 'customer.profile.view'],
  },
  {
    to: '/revenue/receipts',
    label: 'Receipts',
    permissions: ['revenue.view', 'customer.profile.view'],
  },
];

const PROCUREMENT_ITEMS: FinanceItem[] = [
  {
    to: '/procurement/suppliers',
    label: 'Suppliers',
    permissions: ['procurement.view', 'procurement.suppliers.view', 'supplier.orders.view'],
  },
  { to: '/procurement/requests', label: 'Requests', permissions: ['procurement.view'] },
  {
    to: '/procurement/orders',
    label: 'Orders',
    permissions: ['procurement.view', 'supplier.orders.view', 'supplier.documents.view'],
  },
  {
    to: '/procurement/receiving',
    label: 'Receiving',
    permissions: ['procurement.view', 'supplier.orders.view', 'supplier.documents.view'],
  },
  {
    to: '/procurement/payments',
    label: 'Payments',
    permissions: ['procurement.view', 'supplier.orders.view', 'supplier.documents.view'],
  },
];

const SHOW_RESOURCES_NAV = true;

const RESOURCES_ITEMS: FinanceItem[] = [
  { to: '/resources/assets', label: 'Assets', permissions: ['resources.view'] },
  { to: '/resources/facilities', label: 'Facilities', permissions: ['resources.view'] },
  { to: '/resources/bookings', label: 'Bookings', permissions: ['resources.view'] },
  { to: '/resources/inventory', label: 'Inventory', permissions: ['resources.view'] },
];

// Every self-service identity role (see RoleCatalog::definitions()'s
// "Self-service identities" block) — Announcements/Events are hidden from
// all of them, even though their own routes carry no permission gate (see
// SELF_SERVICE_NAV_ROLES's usage below).
const SELF_SERVICE_NAV_ROLES = ['employee', 'member', 'supplier', 'customer', 'employer'];

// General Ledger Interface (Module 5) — a finance concern, folded into the
// existing Finance & Accounting group rather than a new top-level one.
const GL_INTERFACE_ITEMS: FinanceItem[] = [
  { to: '/gl/mappings', label: 'Account Mappings', permissions: ['gl_interface.configure'] },
  { to: '/gl/batches', label: 'Daily Batches', permissions: ['gl_interface.view'] },
  { to: '/gl/journal-entries', label: 'Correcting Journals', permissions: ['journals.view'] },
];

const BANKING_ITEMS: FinanceItem[] = [
  { to: '/banking/accounts', label: 'Bank Accounts', permissions: ['banking.view'] },
  { to: '/banking/deposits', label: 'Deposits', permissions: ['banking.view'] },
];

const BUDGET_ITEMS: FinanceItem[] = [
  { to: '/budgets', label: 'Budgets', permissions: ['budgets.view'] },
];

// Every `{module}.reports.view` plus the standalone report families that
// aren't tied to one of RoleCatalog::MODULES — used only to decide whether
// the top-level "Reports" link itself is worth showing; ReportsIndex.tsx
// still lists every category regardless of what the viewer can actually
// open (a separate, pre-existing gap this doesn't attempt to fix).
const REPORT_PERMISSIONS = [
  'memberships.reports.view',
  'revenue.reports.view',
  'procurement.reports.view',
  'resources.reports.view',
  'hr.reports.view',
  'investments.reports.view',
  'penalties.reports.view',
  'gl_interface.reports.view',
  'banking.reports.view',
  'budgets.reports.view',
  'consolidated.reports.view',
  'journals.reports.view',
  'memberships.benefits.reports.view',
  'supplier.reports.view',
];

// Each item's `permission` mirrors the exact `permission:...` gate on its
// route in api.php, so a nav link is never shown for something the backend
// would 403 for — and never hidden for a permission it would allow. Every
// item here requires settings.manage. Announcements/Events have no
// permission gate of their own (open to every authenticated user, matching
// their unguarded routes in api.php) — they're spliced into
// visibleSystemItems separately, gated on showAnnouncementsAndEvents
// instead of a permission string, so they don't need an entry (or a
// `permission` field) here.
const SYSTEM_ITEMS: (NavGroupItem & { permission?: string })[] = [
  { to: '/system/accounting-periods', label: 'Accounting Periods', permission: 'settings.manage' },
  { to: '/system/audit-log', label: 'Audit Log', permission: 'settings.manage' },
  { to: '/system/authentication', label: 'Authentication', permission: 'settings.manage' },
  { to: '/system/benefit-plans', label: 'Benefit Plans', permission: 'settings.manage' },
  {
    to: '/memberships/benefits/approval-levels',
    label: 'Benefit Approval Levels',
    permission: 'memberships.benefits.configure',
  },
  { to: '/system/benefit-providers', label: 'Benefit Providers', permission: 'settings.manage' },
  { to: '/system/benefit-types', label: 'Benefit Types', permission: 'settings.manage' },
  { to: '/system/chart-of-accounts', label: 'Chart of Accounts', permission: 'settings.manage' },
  { to: '/system/competencies', label: 'Competencies', permission: 'settings.manage' },
  { to: '/system/exchange-rates', label: 'Exchange Rates', permission: 'settings.manage' },
  { to: '/system/jobs', label: 'Jobs', permission: 'settings.manage' },
  { to: '/system/leave-carry-over-rules', label: 'Leave Carry-Over Rules', permission: 'settings.manage' },
  { to: '/system/leave-entitlement-policies', label: 'Leave Entitlement Policies', permission: 'settings.manage' },
  { to: '/system/lists', label: 'Lists', permission: 'settings.manage' },
  { to: '/system/member-checklist-items', label: 'Member Checklist Items', permission: 'settings.manage' },
  { to: '/system/org-units', label: 'Org Units', permission: 'settings.manage' },
  { to: '/system/overtime-rate-types', label: 'Overtime Rate Types', permission: 'settings.manage' },
  { to: '/system/pay-components', label: 'Pay Components', permission: 'settings.manage' },
  { to: '/system/pay-grades', label: 'Pay Grades', permission: 'settings.manage' },
  { to: '/system/performance-kpis', label: 'Performance KPIs', permission: 'settings.manage' },
  { to: '/system/performance-kras', label: 'Performance KRAs', permission: 'settings.manage' },
  { to: '/system/performance-review-policies', label: 'Performance Review Policies', permission: 'settings.manage' },
  { to: '/system/separation-checklist-items', label: 'Separation Checklist Items', permission: 'settings.manage' },
  { to: '/system/settings', label: 'Settings', permission: 'settings.manage' },
  { to: '/system/templates', label: 'Templates', permission: 'settings.manage' },
  { to: '/system/work-shifts', label: 'Work Shifts', permission: 'settings.manage' },
  { to: '/users', label: 'Users', permission: 'users.manage' },
];

function NavIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      className="nav-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

// Builds one sub-section of the Finance & Accounting group: a header
// divider followed by whichever of its items the user can actually reach.
// Returns an empty array (no header either) when none apply, so an
// Operator/Viewer role that only covers e.g. Resources doesn't see empty
// "Revenue"/"Procurement" headers above the items they can use.
function financeSection(
  header: string,
  items: FinanceItem[],
  user: { permissions?: string[] } | null | undefined
): NavGroupEntry[] {
  const visible = items.filter((item) => hasAnyPermission(user, item.permissions));
  return visible.length > 0 ? [{ header }, ...visible] : [];
}

export function AppShell() {
  const { user, hasPermission, hasRole, logout } = useAuth();
  const { settings } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setSidebarOpen(false), [location.pathname]);

  const showMemberships = hasAnyPermission(user, [
    'memberships.view', 'member.profile.view', 'employer.contributions.view',
    'penalties.view', 'employer.penalties.view',
    'memberships.benefits.view', 'memberships.benefits.configure', 'memberships.benefits.disburse', 'memberships.benefits.cheque.sign',
  ]);
  const showInvestments = hasPermission('investments.view');
  const showReports = hasAnyPermission(user, REPORT_PERMISSIONS);
  const showAnnouncementsAndEvents = !SELF_SERVICE_NAV_ROLES.some((role) => hasRole(role));
  const visibleMembershipItems = MEMBERSHIP_ITEMS.filter((item) => !item.permissions || hasAnyPermission(user, item.permissions));

  // Same OR-list as the /analytics/finance/stats route's own gate in
  // api.php — anyone who can see at least one Finance & Accounting
  // sub-module also sees the Dashboard link, shown before every sub-header.
  const financeItems: NavGroupEntry[] = [
    ...(hasAnyPermission(user, [
      'revenue.view', 'procurement.view', 'resources.view', 'gl_interface.view', 'banking.view', 'budgets.view',
    ])
      ? [{ to: '/finance/dashboard', label: 'Dashboard' }]
      : []),
    ...financeSection('Revenue', REVENUE_ITEMS, user),
    ...financeSection('Procurement', PROCUREMENT_ITEMS, user),
    // Hidden from the nav for now — flip SHOW_RESOURCES_NAV to bring it
    // back. The pages/routes/permissions themselves are untouched.
    ...(SHOW_RESOURCES_NAV ? financeSection('Resources', RESOURCES_ITEMS, user) : []),
    ...financeSection('General Ledger', GL_INTERFACE_ITEMS, user),
    ...financeSection('Banking', BANKING_ITEMS, user),
    ...financeSection('Budgets', BUDGET_ITEMS, user),
  ];

  const visibleSystemItems = [
    ...(showAnnouncementsAndEvents ? [{ to: '/announcements', label: 'Announcements' }, { to: '/events', label: 'Events' }] : []),
    ...SYSTEM_ITEMS.filter((item) => !item.permission || hasPermission(item.permission)),
  ];
  const visibleHrItems = HR_ITEMS.filter((item) => !item.permissions || hasAnyPermission(user, item.permissions));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="layout">
      <header className="app-header">
        <div className="app-header-left">
          <button
            type="button"
            className="menu-toggle"
            aria-label="Toggle navigation"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>

          <Link to="/" className="brand">
            <img className="brand-mark" src={settings.app_icon_url ?? vnpfLogo} alt="" />
            <h1>{settings.app_name}</h1>
          </Link>
        </div>

        <AccountMenu user={user} onLogout={handleLogout} />
      </header>

      <div className="layout-body">
        {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

        <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
          <nav>
            <NavLink to="/" end>
              <NavIcon>
                <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </NavIcon>
              Home
            </NavLink>

            <NavLink to="/library">
              <IconAttach className="nav-icon" />
              Library
            </NavLink>

            {showMemberships && (
              <NavGroup icon={IconMembers} label="Memberships" basePath="/memberships" items={visibleMembershipItems} />
            )}

            {financeItems.length > 0 && (
              <NavGroup
                icon={IconFinance}
                label="Finance & Accounting"
                basePath={['/revenue', '/procurement', '/resources', '/gl']}
                items={financeItems}
              />
            )}

            {visibleHrItems.length > 0 && (
              <NavGroup
                icon={IconHR}
                // hr.view holders administer everyone's records — that's a
                // real "Human Resources" module. Someone who only holds
                // employee.profile.view sees the same self-service items
                // (their own Attendance/Payslips/etc.), but calling it
                // "Human Resources" overstates what they can actually do —
                // labeled "Employee" instead.
                label={hasPermission('hr.view') ? 'Human Resources' : 'Employee'}
                basePath={['/hr']}
                items={visibleHrItems}
              />
            )}

            {showInvestments && (
              <NavGroup
                icon={IconInvestments}
                label="Investment Management"
                basePath="/investments"
                items={INVESTMENT_ITEMS}
              />
            )}

            {showReports && (
              <NavLink to="/reports">
                <IconReports className="nav-icon" />
                Reports
              </NavLink>
            )}

            {visibleSystemItems.length > 0 && (
              <NavGroup
                icon={IconSystem}
                label="System"
                basePath={['/users', '/system', '/announcements', '/events']}
                items={visibleSystemItems}
              />
            )}
          </nav>
        </aside>

        <div className="main-column">
          <main className={`content${location.pathname.startsWith('/reports/') ? ' content-wide' : ''}`}>
            {/* Keyed by path so a page that errors doesn't keep showing the
                error message after the user navigates away from it. */}
            <ErrorBoundary key={location.pathname}>
              <Suspense fallback={<PageLoading />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </main>

          <footer className="app-footer">
            <span>&copy; {new Date().getFullYear()} Vanuatu National Provident Fund</span>
            <NavLink to="/changelog">v{CURRENT_VERSION}</NavLink>
          </footer>
        </div>
      </div>
    </div>
  );
}
